/*!
 * Define custom prototype functions and polyfills.
 * Key is the function name, value is an array of two arguments.
 * Array[0] is the function property object. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperty
 * Array[1] (optional, Array by default) is the prototype to define to. (Array, Object, String etc.)
 */
const Polyfills = {
    // Allow a ChildNode to remove itself.
    // https://developer.mozilla.org/en-US/docs/Web/API/ChildNode/remove
    remove: [
        {
            configurable: true,
            enumerable: true,
            writable: true,
            value: function remove() {
                this?.parentNode?.removeChild?.(this);
            },
        },
        [
            Element.prototype,
            CharacterData.prototype,
            DocumentType.prototype,
        ],
    ],
    // Get the majority value of a truthy filled array.
    // [ true, true, false ] == true
    // [ false, false, true ] == false
    // [ true, true, false, false ] == true
    majority: [
        {
            value: function () { return this.filter(v => v).length >= this.filter(v => !v).length },
        },
    ],
    // Chunk an array and map it to the provided function.
    chunk: [
        {
            value: function (fn = null, s = 15, r = []) {
                while (this.length) { r.push(this.splice(0, s)) }
                return fn ? r.map(fn) : r;
            },
        },
    ],
    // Recursive flatten an array.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat
    flat: [
        {
            value: function (count = 0) {
                let flat;
                return (flat = (_this, cur = 0) => {
                    return _this.reduce((a, c) => a.concat(Array.isArray(c) && count > ++cur ? flat(c, cur) : c), []);
                })(this);
            },
        },
    ],
    // Create an object from an iterable.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/fromEntries
    fromEntries: [
        {
            value: function (iter) {
                return [...iter].reduce((obj, [key, val]) => {
                    obj[key] = val;
                    return obj
                }, {});
            },
        },
        Object.prototype,
        () => !(Object.fromEntries instanceof Function),
    ],
    // Convert a Map() to JSON string. Map.JSON()
    JSON: [
        {
            value: function (data) {
                return JSON.stringify(data || this, (k, v) => v instanceof Set ? Array.from(v) : (v instanceof Map ? Object.fromEntries(v) : v));
            },
        },
        Map.prototype,
    ],
}

/*
 * Convert a polyfill to manual.
 *      Polyfill('chunk')([1, 2, 3, 4, 5], null, 2);
 */
function Polyfill(poly) {
    return (input, ...args) => Polyfills[poly]?.[0]?.value?.apply?.(input, args);
}

// Returns a promise, which returns a list of polyfilled functions.
function Inject(excludes = false) {
    if ((function () { return !this })()) {
        return Promise.reject(console.warn([
            'Strict mode is enabled; can not inject prototype polyfills! Please use the `Polyfill(< name >)(< input [, arguments ]>)` converter.',
            'Optionally, if required, add "@corefact/toolchain/helpers/removeStrict" to your webpack config.',
        ].join('\n')));
    }

    return new Promise((res, rej) => {
        let patched = {};

        Object.entries(Polyfills).forEach(([key, [obj, protos = [Array.prototype], test = (proto, key) => !Object.prototype.hasOwnProperty.call(proto, key)]]) => {
            if (((Array.isArray(excludes) && excludes.indexOf(key) > -1)) || excludes == key) return false;
            (Array.isArray(protos) ? protos : [protos]).forEach(proto => {
                if (test(proto, key) && !proto[key]) {
                    try {
                        Object.defineProperty(proto, key, obj);
                        patched[key] = true;
                    } catch (error) {
                        console.warn(`Could not patch ${key}:`)
                        console.trace(error);
                    }
                }
            });
        });

        return Object.keys(patched).length >= 1 ? res(patched) : rej();
    });
}

export {
    Inject,
    Polyfills as default,
    Polyfill,
}
